/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import { Autocomplete, Avatar, Chip } from '@mui/material';
import { useDebounce } from 'use-debounce';

import type { PartialUser } from '../../../../../Data/types';
import { SEARCH_USERS } from '../../../../../graphql/queries';
import { generateUserImageUrl } from '../../../../../helpers/userImagesUrlUtils';
import AutocompleteInput from '../AutocompleteInput/AutocompleteInput';

type Props = {
  onChange?: (value: PartialUser[]) => void;
  required?: boolean;
  value?: PartialUser[] | null;
  error?: boolean;
  readOnly?: boolean;
};

const getDisplayName = (option: PartialUser | string): string =>
  typeof option === 'string' ? option : option.DisplayName || '';

const getImageUrl = (option: PartialUser | string): string =>
  typeof option === 'string' ? '' : option.ImageUrlSmall || '';

const getKey = (option: PartialUser | string): string =>
  typeof option === 'string' ? option : option.ADSystemID || '';

const PeoplePicker = ({ onChange, required, value, error, readOnly }: Props) => {
  const [searchUsers, { loading }] = useLazyQuery(SEARCH_USERS);
  const [query, setQuery] = useState<string>('');
  const [debouncedValue] = useDebounce(query, 400);
  const [peopleOptions, setPeopleOptions] = useState<PartialUser[]>(value || []);
  useEffect(() => {
    if (readOnly) return;
    if (!debouncedValue) {
      setPeopleOptions([]);
      return;
    }
    searchUsers({
      variables: {
        searchString: `%${debouncedValue}%`,
        limit: 5,
      },
    })
      .then((res) => {
        const users =
          res.data?.mlfsl_personnel?.map((p) => {
            return {
              ADSystemID: p.ActiveDirectoryLogin,
              DisplayName: p.NamePreferredDisplay,
              ImageUrlLarge: p.Email ? generateUserImageUrl(p.Email, 'L') : undefined,
              ImageUrlMedium: p.Email ? generateUserImageUrl(p.Email, 'M') : undefined,
              ImageUrlSmall: p.Email ? generateUserImageUrl(p.Email, 'S') : undefined,
              WorkEmail: p.Email,
              DirectWorkPhone: p.BusinessPrimaryOfficePhoneNumberFull,
              DisplayTitle: p.TitleFullyQualified,
              PracticeGrpName: p.PrimaryPracticeGroup?.SubSubPracticeGroupName,
              PeopleOfficeAddress: p.BusinessPrimaryOfficeAddress1,
              OfficeName: p.BusinessPrimaryOffice,
              PracticeGrpRollupName: p.PrimaryPracticeGroup?.PracticeGroupName,
            } as PartialUser;
          }) ?? [];
        return setPeopleOptions(users);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [debouncedValue, searchUsers]);

  return (
    <Autocomplete
      handleHomeEndKeys
      disableCloseOnSelect
      multiple
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
      freeSolo
      options={peopleOptions}
      value={value || []}
      readOnly={readOnly}
      fullWidth
      limitTags={2}
      renderInput={(params) => (
        <AutocompleteInput
          params={params}
          placeholder={readOnly ? '' : 'Search people'}
          required={required}
          error={error}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
        />
      )}
      noOptionsText=""
      loadingText="Loading..."
      loading={loading}
      onChange={(_e, newValue) => {
        if (onChange) {
          onChange(newValue as PartialUser[]);
        }
      }}
      getOptionLabel={getDisplayName}
      renderValue={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            avatar={
              <Avatar
                alt={getDisplayName(option)}
                src={getImageUrl(option)}
                sx={{ width: 30, height: 30, marginRight: 1 }}
              />
            }
            label={getDisplayName(option)}
            {...getTagProps({ index })}
            key={getKey(option)}
            sx={{ marginRight: 1 }}
          />
        ))
      }
      renderOption={(props, option) => (
        <li {...props} key={getKey(option)}>
          <Avatar
            alt={getDisplayName(option)}
            src={getImageUrl(option)}
            sx={{ width: 30, height: 30, marginRight: 1 }}
          />
          {getDisplayName(option)}
        </li>
      )}
    />
  );
};
export default PeoplePicker;
